import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { Link } from 'react-router-dom';
import {
  Home,
  ArrowRight,
  CheckCircle,
  TrendingUp,
  Search,
  Brain,
  Map,
  DollarSign,
  Users,
  Target,
  BarChart3,
  Building2,
  MapPin,
  Shield,
  Zap,
  Calculator,
  Handshake,
  Play,
  ChevronRight,
  Lightbulb
} from 'lucide-react';
import { ImageWithFallback } from '../../figma/ImageWithFallback';
import { ProductTestimonials } from '../../shared/ProductTestimonials';
import { realEstateTestimonials } from '../../shared/testimonialData';

const RealEstate: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);

  const features = [
    {
      icon: Search,
      title: 'Intelligent Search Platform',
      description: '	AI-powered discovery engine that ranks listings based on buyer preferences, budget, location, and behavior. 	Map-based campaigns with filters for localities, amenities, commute time, and investment potential.'
    },
    {
      icon: Brain,
      title: 'Enhanced User Experience',
      description: 'A single interface for browsing, shortlisting, visiting, and financing properties. 	Rich visuals, maps, and neighborhood insights that help buyers make confident decisions.'
    },
    {
      icon: Map,
      title: 'Real Estate AI Flow',
      description: '	Automated listings ingestion from multiple sources and channels. 	Buyer profiling, AI matching, and scoring of leads based on intent and fit.'
    },
    {
      icon: DollarSign,
      title: 'Scalable Solutions',
      description: '	Architecture designed to support enterprise volumes, multiple brands, and regional teams. 	Ability to plug into existing portals, marketing systems, and loan engines.'
    },
    {
      icon: BarChart3,
      title: 'Personalized Experience & Memory Layer',
      description: '	Stores and learns from buyer interactions searches, shortlists, visits, and conversations. 	Uses this memory to personalize follow-up interactions, nudges, and recommendations.'
    },

  ];

  const solutions = [
    {
      icon: Search,
      title: '	Developer Sales Transformation',
      description: '	Centralize inventory across projects and cities. 	Use AI lead scoring to prioritize serious buyers, increasing site visits and bookings.',
      features: ['Natural Language Search', 'Smart Filters', 'Personalized Ranking', 'Saved Searches', 'Instant Alerts']
    },
    {
      icon: MapPin,
      title: '	Brokerage Network Productivity',
      description: '	Provide brokers a single workspace to manage listings, leads, and follow-ups. 	Reward quality brokers with better inventory visibility and shared closure analytics.',
      features: ['Interactive Maps', 'Neighborhood Data', 'School Ratings', 'Transit Access', 'Amenity Finder']
    },
    {
      icon: Calculator,
      title: '	Bank & NBFC Home Loan Origination',
      description: '	Embed pre-approved offers and eligibility checks directly into the property journey. 	Reduce drop-offs by keeping property discovery and financing in one flow.',
      features: ['Instant Pre-Qualification', 'Multiple Lenders', 'Rate Comparison', 'Mortgage Calculator', 'Application Tracking']
    },
    {
      icon: Brain,
      title: '	Insurer & Relocation Programs',
      description: '	Offer curated property options for corporate relocation and insured clients. 	Track outcomes, satisfaction, and turnaround time in a single dashboard.',
      features: ['Preference Learning', 'Match Scoring', 'Smart Notifications', 'Behavioral Analysis', 'Predictive Suggestions']
    },
        {
      icon: Brain,
      title: '	Marketing Optimization for Real Estate Portals',
      description: '	Run targeted map campaigns by locality, buyer segment, or budget band.	Use predictive analytics to understand which channels and creatives generate the most conversions.',
      features: ['Preference Learning', 'Match Scoring', 'Smart Notifications', 'Behavioral Analysis', 'Predictive Suggestions']
    },
        {
      icon: Target,
      title: '		Investment & Portfolio Insights',
      description: 'Provide investors with price trends, rental yields, and risk indicators. 	Support data-backed decision-making for bulk purchases or portfolio rebalancing.',
      features: ['Preference Learning', 'Match Scoring', 'Smart Notifications', 'Behavioral Analysis', 'Predictive Suggestions']
    },

  ];

  const compliances = [
    {
      icon: Users,
      title: '	Role-Based Access Control',
      description: 'Different views and permissions for buyers, brokers, developers, lenders, and admins. Sensitive data is only visible to those who need it.',
      benefits: ['Smart Property Discovery', 'Instant Financing', 'Market Insights', 'Virtual Tours', 'Offer Management']
    },
    {
      icon: Building2,
      title: '	Data Segregation',
      description: 'Tenant-wise separation for different developers, banks, or partner organizations, preventing cross-client data exposure.',
      benefits: ['Project Showcasing', 'Lead Generation', 'Analytics Dashboard', 'Buyer Insights', 'Inventory Management']
    },
    {
      icon: Handshake,
      title: '	Secure Transmission & Storage',
      description: 'All critical data is transmitted over encrypted channels and stored securely with strict access controls.',
      benefits: ['Listing Management', 'Lead Tracking', 'CRM Integration', 'Commission Calculator', 'Document Management']
    },
    {
      icon: DollarSign,
      title: 'Audit Trails & Monitoring',
      description: 'Detailed logs of key actions (lead updates, approvals, document access) support audit requirements and dispute resolution.',
      benefits: ['Qualified Leads', 'Application Processing', 'Rate Management', 'Compliance Tools', 'Portfolio Analytics']
    },
        {
      icon: Shield,
      title: 'Compliance-Ready Design',
      description: 'Built to align with prevailing data-privacy norms and financial-sector expectations in areas like consent management, data retention, and reporting. This approach gives enterprises the confidence to run core sales and loan processes on the platform without risking trust or compliance.',
      benefits: ['Qualified Leads', 'Application Processing', 'Rate Management', 'Compliance Tools', 'Portfolio Analytics']
    },
  ];

  const benefits = [
    {
      icon: Zap,
      title: 'For Developers and Real Estate Firms',
      description: '	Shorter sales cycles through better-qualified leads and integrated financing. Higher utilization of marketing budgets with clear insight into which campaigns drive bookings.',
    },
    {
      icon: TrendingUp,
      title: 'For Brokers and Channel Partners',
      description: 'Access to richer inventory and smarter tools for matching buyers to properties. 	CRM-lite capabilities for managing follow-ups, tasks, and documentation.',
    },
    {
      icon: Target,
      title: 'For Banks, NBFCs, and Insurers',
      description: '	Higher-quality loan and insurance prospects directly at the point of property discovery. 	Better risk evaluation using property, buyer, and behavioral data.',
    },
    {
      icon: Shield,
      title: 'For Buyers and Corporate Clients',
      description: '	Faster, simpler journey from search to closing, with fewer touchpoints and surprises. 	Personalized recommendations that match budget, lifestyle, and long-term value.',
    }
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div
        style={{ y: backgroundY }}
        className="fixed inset-0 opacity-20"
      >
        <div className="absolute inset-0">
          {Array.from({ length: 8 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-6 h-6 bg-blue-500 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 7,
                delay: i * 0.6,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 neon-glow">
                  <Home className="w-8 h-8 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-blue-400 uppercase tracking-wide">Intelligent Search Platform</span>
                  <h1 className="text-4xl lg:text-6xl font-bold">
                    <span className="bg-gradient-to-r from-blue-500 to-cyan-600 bg-clip-text text-transparent">Real Estate AI</span>
                  </h1>
                  <p className="text-sm text-muted-foreground italic">Property Intelligence Platform</p>
                </div>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-muted-foreground mb-8 leading-relaxed text-justify"
              >
               The AI-powered real estate platform is built to simplify how properties are discovered, evaluated, and financed. It connects buyers, developers, brokers, banks, NBFCs, and insurers on a single intelligent search layer that understands demand, ranks listings, and keeps every stakeholder in sync. From AI-ranked listings and map campaigns to integrated loan approvals and closing analytics, the platform turns a fragmented property journey into a smooth, data-driven experience. Real estate firms can move faster, reduce leakage in the funnel, and deliver a modern digital journey that matches how customers actually buy homes today.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-blue-500 to-cyan-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-blue-500/25 transition-all duration-300"
                >
                  Request Platform Demo
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300">
                  <Play className="w-5 h-5" />
                  Watch Demo
                </button>
              </motion.div>

              {/* Key Metrics */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="grid grid-cols-3 gap-8 hidden"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-500">AI</div>
                  <div className="text-sm text-muted-foreground">Powered</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-cyan-400">Smart</div>
                  <div className="text-sm text-muted-foreground">Matching</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent">Instant</div>
                  <div className="text-sm text-muted-foreground">Financing</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <div className="relative group">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
                  className="absolute -inset-4 bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 rounded-2xl blur-3xl opacity-20"
                />
                <div className="relative overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
                  <ImageWithFallback
                    src="/assets/Products/ai-Products/RealEstateAI.png"
                    alt="Real Estate AI Platform"
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Core <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Features</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Comprehensive platform features designed for modern real estate operations
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-6 hover:scale-105 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 text-blue-400" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-white">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground text-justify">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Flowchart Section - Using Provided Image */}
      <section className="relative py-24 bg-white/[0.02] hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Platform <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Workflow</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              End-to-end intelligent property discovery and transaction process
            </p>
          </motion.div>

          {/* Hardcoded Flowchart from Image */}
          <div className="relative">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-6 relative">
              {/* Step 1: Property Data Collection */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="glass-strong rounded-xl p-6 relative group hover:border-blue-500/50 transition-all duration-300"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center mb-4 shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform duration-300">
                    <Search className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-white">Property Data Collection</h3>
                  <p className="text-sm text-muted-foreground">Gathering property information and market data</p>
                </div>
                {/* Arrow */}
                <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                  <ChevronRight className="w-6 h-6 text-blue-400" />
                </div>
              </motion.div>

              {/* Step 2: AI Analysis */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="glass-strong rounded-xl p-6 relative group hover:border-cyan-500/50 transition-all duration-300"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-400 to-teal-500 flex items-center justify-center mb-4 shadow-lg shadow-cyan-500/30 group-hover:scale-110 transition-transform duration-300">
                    <Lightbulb className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-white">AI Analysis</h3>
                  <p className="text-sm text-muted-foreground">Machine learning processes property features and trends</p>
                </div>
                {/* Arrow */}
                <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                  <ChevronRight className="w-6 h-6 text-cyan-400" />
                </div>
              </motion.div>

              {/* Step 3: Valuation & Insights */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="glass-strong rounded-xl p-6 relative group hover:border-teal-500/50 transition-all duration-300"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center mb-4 shadow-lg shadow-teal-500/30 group-hover:scale-110 transition-transform duration-300">
                    <TrendingUp className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-white">Valuation & Insights</h3>
                  <p className="text-sm text-muted-foreground">Predictive analytics for pricing and investment potential</p>
                </div>
                {/* Arrow */}
                <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                  <ChevronRight className="w-6 h-6 text-teal-400" />
                </div>
              </motion.div>

              {/* Step 4: Smart Matching */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="glass-strong rounded-xl p-6 relative group hover:border-emerald-500/50 transition-all duration-300"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center mb-4 shadow-lg shadow-emerald-500/30 group-hover:scale-110 transition-transform duration-300">
                    <Users className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-white">Smart Matching</h3>
                  <p className="text-sm text-muted-foreground">AI-powered property recommendations based on preferences</p>
                </div>
                {/* Arrow */}
                <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                  <ChevronRight className="w-6 h-6 text-emerald-400" />
                </div>
              </motion.div>

              {/* Step 5: Transaction Support */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="glass-strong rounded-xl p-6 relative group hover:border-green-500/50 transition-all duration-300"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-400 to-lime-500 flex items-center justify-center mb-4 shadow-lg shadow-green-500/30 group-hover:scale-110 transition-transform duration-300">
                    <Handshake className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-white">Transaction Support</h3>
                  <p className="text-sm text-muted-foreground">Streamlined closing with AI-assisted documentation</p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Capabilities Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
               <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent"> Solutions</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-justify">
              Advanced technology stack designed for modern real estate marketplace
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-8 hover:scale-105 transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-7 h-7 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-xl mb-2 text-white">{solution.title}</h3>
                      <p className="text-sm text-muted-foreground text-justify">{solution.description}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 hidden">
                    {solution.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* User Roles Section */}
      <section className="relative py-24 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Data Security & <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent"> Compliance</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Handling property, financial, and identity data demands strong safeguards. The platform is designed with enterprise-grade security in mind.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {compliances.map((compliance, index) => {
              const Icon = compliance.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-8 hover:scale-105 transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-7 h-7 text-cyan-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-xl mb-2 text-white">{compliance.title}</h3>
                      <p className="text-sm text-muted-foreground text-justify">{compliance.description}</p>
                    </div>
                  </div>
                  <ul className="space-y-2 hidden">
                    {compliance.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Platform <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Benefits</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Transform the real estate experience with intelligent automation
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass rounded-xl p-6 border-l-4 border-blue-500/50 hover:border-blue-500 transition-all duration-300"
                >
                  <Icon className="w-8 h-8 text-blue-400 mb-3" />
                  <h3 className="font-semibold text-lg mb-2 text-white">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3 text-justify">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative py-24 bg-white/[0.02] hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ProductTestimonials 
            testimonials={realEstateTestimonials}
            title="Real Estate Excellence"
            subtitle="See how organizations transformed property operations with AI-powered platform"
            gradientFrom="#00CFFF"
            gradientVia="#0FFFA0"
            gradientTo="#00CFFF"
            iconColor="#00CFFF"
            dotColor="bg-cyan-500"
            speed={65}
            direction="left"
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-strong rounded-2xl p-12 text-center border border-blue-500/30 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-cyan-500/10" />
            <div className="relative z-10">
              <h2 className="text-3xl lg:text-4xl mb-4 text-white">
                Ready to Transform Your <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Real Estate Business?</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto text-justify">
                Schedule a demo to see how our AI-powered platform can revolutionize property search, matching, and transaction processing for your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-blue-500 to-cyan-600 text-white px-8 py-4 rounded-xl font-semibold shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-300"
                >
                  Schedule Demo
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl font-semibold border border-blue-500/30 hover:border-blue-500/50 transition-all duration-300 text-white"
                >
                  Contact Sales
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default RealEstate;